import { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, extractError } from "@/lib/api";
import { PageContainer, PageHeader, EmptyState } from "@/components/app/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { DynamicFormRenderer } from "@/components/app/DynamicFormRenderer";
import { RecordCommentsThread } from "@/components/app/RecordCommentsThread";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
    DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/contexts/AuthContext";
import { Database, Plus, ArrowLeft, MagnifyingGlass, Trash, FileXls, FilePdf, Sigma, X, ChatCircle, CaretUp, CaretDown, Funnel, Eye, ListChecks, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";
import CsvImportEditor from "@/components/app/CsvImportEditor";

const BASE = process.env.REACT_APP_BACKEND_URL;

/** Sheets-style draggable resize handle anchored to the right edge of a <th>. */
function ColumnResizer({ fieldName, width, setWidth }) {
    const onMouseDown = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const startX = e.clientX;
        const startW = width;
        const onMove = (ev) => {
            const dx = ev.clientX - startX;
            setWidth(fieldName, startW + dx);
        };
        const onUp = () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
        };
        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";
        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
    };
    return (
        <span
            role="separator"
            aria-orientation="vertical"
            onMouseDown={onMouseDown}
            onClick={(e) => e.stopPropagation()}
            onDoubleClick={(e) => { e.stopPropagation(); setWidth(fieldName, 160); }}
            className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40 active:bg-primary/60 transition-colors"
            data-testid={`col-resizer-${fieldName}`}
            title="Drag to resize · double-click to reset"
        />
    );
}

async function downloadFile(url, fallbackName) {
    try {
        const res = await fetch(`${BASE}${url}`, { credentials: "include" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const cd = res.headers.get("content-disposition") || "";
        const match = /filename="?([^";]+)"?/.exec(cd);
        const fname = match ? match[1] : fallbackName;
        const blob = await res.blob();
        const link = document.createElement("a");
        const href = URL.createObjectURL(blob);
        link.href = href; link.download = fname;
        document.body.appendChild(link); link.click(); link.remove();
        setTimeout(() => URL.revokeObjectURL(href), 1000);
    } catch (e) {
        toast.error("Export failed: " + e.message);
    }
}

export default function FormRecordsPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const isAdmin = user?.role === "platform_admin" || user?.role === "org_admin";
    const currentUserId = user?.id;
    const [perms, setPerms] = useState({ can_create: false, can_update: false, can_delete: false, can_read: false, is_admin: false });
    const [form, setForm] = useState(null);
    const [sections, setSections] = useState([]);
    const [fields, setFields] = useState([]);
    const [groupDefaults, setGroupDefaults] = useState({});
    const [records, setRecords] = useState([]);
    const [aggResults, setAggResults] = useState([]); // [{op, field, value}]
    const [aggTotal, setAggTotal] = useState(0);
    const [formulasOpen, setFormulasOpen] = useState(false);
    // formula list: [{ op: "sum"|"avg"|"min"|"max"|"count", field: "amount" }]
    const FORMULA_KEY = `formflo:formulas:${id}`;
    const [formulas, setFormulas] = useState([]);

    // Re-load formulas whenever the form id changes (component reuse across routes).
    useEffect(() => {
        try {
            const raw = localStorage.getItem(`formflo:formulas:${id}`);
            setFormulas(raw ? JSON.parse(raw) : []);
        } catch {
            setFormulas([]);
        }
    }, [id]);
    const [draftOp, setDraftOp] = useState("sum");
    const [draftField, setDraftField] = useState("");
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(20);
    const [search, setSearch] = useState("");
    const [sortBy, setSortBy] = useState("");
    const [sortDir, setSortDir] = useState(-1);
    const [filterChips, setFilterChips] = useState([]); // [{ id, field, op, value }]
    const [addingFilter, setAddingFilter] = useState(false);
    const [filterDraftField, setFilterDraftField] = useState("");
    const [filterDraftOp, setFilterDraftOp] = useState("contains");
    const [filterDraftValue, setFilterDraftValue] = useState("");
    const [filterDraftValue2, setFilterDraftValue2] = useState(""); // for "between"
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [recData, setRecData] = useState({});

    // CSV interactive editor
    const [csvEditorOpen, setCsvEditorOpen] = useState(false);
    const [csvEditorInitial, setCsvEditorInitial] = useState(null);

    // Resizable column widths (Sheets-style) — persisted per form in localStorage.
    const COLWIDTH_KEY = `formflo:colwidths:${id}`;
    const [colWidths, setColWidths] = useState({});
    useEffect(() => {
        try {
            const raw = localStorage.getItem(`formflo:colwidths:${id}`);
            setColWidths(raw ? JSON.parse(raw) : {});
        } catch (err) {
            console.warn("Could not restore column widths from localStorage", err);
            setColWidths({});
        }
    }, [id]);
    const setColWidth = (fieldName, w) => {
        setColWidths((prev) => {
            const next = { ...prev, [fieldName]: Math.max(80, Math.round(w)) };
            try { localStorage.setItem(COLWIDTH_KEY, JSON.stringify(next)); } catch { /* ignore */ }
            return next;
        });
    };

    const loadMeta = async () => {
        try {
            const [meta, p] = await Promise.all([
                api.get(`/forms/${id}`),
                api.get(`/forms/${id}/permissions`).catch(() => ({ data: { can_create: false, can_update: false, can_delete: false, can_read: true, is_admin: false } })),
            ]);
            setForm(meta.data.form); setSections(meta.data.sections); setFields(meta.data.fields);
            setPerms(p.data);
        } catch (e) { toast.error(extractError(e)); }
    };
    const loadRecords = async () => {
        try {
            const params = { page, page_size: pageSize };
            if (search) params.q = search;
            if (sortBy) { params.sort_by = sortBy; params.sort_dir = sortDir; }
            const activeChips = filterChips.filter((c) => c.op === "is_empty" || c.op === "is_not_empty" || (Array.isArray(c.value) ? c.value.some((v) => v !== "" && v != null) : c.value !== "" && c.value != null));
            if (activeChips.length) {
                params.filters = JSON.stringify(activeChips.map(({ field, op, value }) => ({ field, op, value })));
            }
            const { data } = await api.get(`/forms/${id}/records`, { params });
            setRecords(data.items); setTotal(data.total);
        } catch (e) { toast.error(extractError(e)); }
    };
    const loadGroupDefaults = async () => {
        try {
            // 1) Effective defaults — covers per-form access configs + legacy group-level
            //    (the server merges them with the right precedence).
            const { data: eff } = await api.get(`/forms/${id}/records/my-defaults`);
            const merged = { ...(eff || {}) };
            setGroupDefaults(merged);
            return merged;
        } catch (err) {
            console.warn("[records] group defaults fetch failed:", err);
            return {};
        }
    };

    useEffect(() => { loadMeta(); loadGroupDefaults(); /* eslint-disable-next-line */ }, [id, user?.id]);
    useEffect(() => { loadRecords(); /* eslint-disable-next-line */ }, [id, page, sortBy, sortDir, filterChips]);

    // Variant state
    const [variants, setVariants] = useState([]);
    const [activeVariantId, setActiveVariantId] = useState(null);
    const [variantColumns, setVariantColumns] = useState(null); // null = auto (default), otherwise array of field_names
    const [variantPickerOpen, setVariantPickerOpen] = useState(false);
    const [saveVariantOpen, setSaveVariantOpen] = useState(false);
    const [saveDraft, setSaveDraft] = useState({ name: "", is_public: false, is_default: false });

    const autoVisibleColumns = useMemo(() =>
        fields.filter((f) => f.filterable || f.sortable || f.searchable || f.required).slice(0, 5),
        [fields]
    );
    const visibleColumns = useMemo(() => {
        if (!variantColumns) return autoVisibleColumns;
        const byName = Object.fromEntries(fields.map((f) => [f.field_name, f]));
        return variantColumns.map((n) => byName[n]).filter(Boolean);
    }, [variantColumns, fields, autoVisibleColumns]);
    const sortableFields = useMemo(() => fields.filter((f) => f.sortable), [fields]);
    const filterableFields = useMemo(() => fields.filter((f) => f.filterable), [fields]);

    // Load variants once we have a form id; auto-apply the default if one exists.
    useEffect(() => {
        if (!id || !fields.length) return;
        (async () => {
            try {
                const { data } = await api.get(`/record-views/${id}`);
                setVariants(data || []);
                const def = (data || []).find((v) => v.is_default);
                if (def && !activeVariantId) {
                    applyVariant(def);
                }
            } catch (err) { console.error("Failed to load record variants", err); }
        })();
        // eslint-disable-next-line
    }, [id, fields.length]);

    const applyVariant = (v) => {
        setActiveVariantId(v.id);
        setVariantColumns(v.columns && v.columns.length ? v.columns : null);
        setFilterChips((v.filters || []).map((f, i) => ({ id: `v-${i}`, ...f })));
        setSortBy(v.sort_by || "");
        setSortDir(v.sort_dir || -1);
        setSearch(v.search || "");
        setPage(1);
    };

    const clearVariant = () => {
        setActiveVariantId(null);
        setVariantColumns(null);
        setFilterChips([]);
        setSortBy(""); setSortDir(-1);
        setSearch("");
        setPage(1);
    };

    const saveVariant = async () => {
        const name = (saveDraft.name || "").trim();
        if (!name) { toast.error("Variant name required"); return; }
        try {
            const body = {
                form_id: id, name,
                is_public: !!saveDraft.is_public, is_default: !!saveDraft.is_default,
                columns: (variantColumns && variantColumns.length) ? variantColumns : visibleColumns.map((c) => c.field_name),
                filters: filterChips.map(({ field, op, value }) => ({ field, op, value })),
                sort_by: sortBy || null, sort_dir: sortDir || -1,
                search: search || null,
            };
            const { data } = await api.post("/record-views", body);
            setVariants([...variants, data]);
            setActiveVariantId(data.id);
            setSaveVariantOpen(false);
            setSaveDraft({ name: "", is_public: false, is_default: false });
            toast.success("View variant saved");
        } catch (e) { toast.error(extractError(e)); }
    };

    const deleteVariant = async (vid) => {
        if (!window.confirm("Delete this variant?")) return;
        try {
            await api.delete(`/record-views/${vid}`);
            setVariants(variants.filter((v) => v.id !== vid));
            if (activeVariantId === vid) clearVariant();
            toast.success("Variant deleted");
        } catch (e) { toast.error(extractError(e)); }
    };

    const toggleColumn = (fieldName) => {
        const current = variantColumns || autoVisibleColumns.map((c) => c.field_name);
        if (current.includes(fieldName)) {
            const next = current.filter((n) => n !== fieldName);
            setVariantColumns(next.length ? next : [fieldName]); // never go to zero columns
        } else {
            setVariantColumns([...current, fieldName]);
        }
    };

    const filterOpsFor = (ft) => {
        switch (ft) {
            case "number":
            case "decimal":
                return [
                    { v: "eq", label: "equals" }, { v: "neq", label: "not equals" },
                    { v: "gt", label: ">" }, { v: "gte", label: "≥" }, { v: "lt", label: "<" }, { v: "lte", label: "≤" },
                    { v: "between", label: "between" },
                    { v: "is_empty", label: "is empty" }, { v: "is_not_empty", label: "is not empty" },
                ];
            case "date":
            case "datetime":
                return [
                    { v: "eq", label: "equals" }, { v: "before", label: "before" }, { v: "after", label: "after" },
                    { v: "between", label: "between" },
                    { v: "is_empty", label: "is empty" }, { v: "is_not_empty", label: "is not empty" },
                ];
            case "dropdown":
            case "radio":
            case "lookup":
                return [
                    { v: "eq", label: "equals" }, { v: "neq", label: "not equals" }, { v: "in", label: "in" },
                    { v: "is_empty", label: "is empty" }, { v: "is_not_empty", label: "is not empty" },
                ];
            case "multiselect":
            case "checkbox":
                return [
                    { v: "contains", label: "contains" }, { v: "not_contains", label: "not contains" },
                    { v: "is_empty", label: "is empty" }, { v: "is_not_empty", label: "is not empty" },
                ];
            case "toggle":
                return [{ v: "eq", label: "equals" }];
            default:
                return [
                    { v: "contains", label: "contains" }, { v: "equals", label: "equals" }, { v: "not_equals", label: "not equals" },
                    { v: "is_empty", label: "is empty" }, { v: "is_not_empty", label: "is not empty" },
                ];
        }
    };

    const filterValueless = (op) => op === "is_empty" || op === "is_not_empty";

    const addFilterChip = () => {
        if (!filterDraftField || !filterDraftOp) { toast.error("Pick field & operator"); return; }
        const isBetween = filterDraftOp === "between";
        if (!filterValueless(filterDraftOp) && !isBetween && (filterDraftValue === "" || filterDraftValue == null)) {
            toast.error("Enter a value"); return;
        }
        const value = isBetween ? [filterDraftValue, filterDraftValue2] : filterDraftValue;
        const chip = { id: `${filterDraftField}-${filterDraftOp}-${Date.now()}`, field: filterDraftField, op: filterDraftOp, value };
        setFilterChips([...filterChips, chip]);
        setAddingFilter(false);
        setFilterDraftField(""); setFilterDraftOp("contains"); setFilterDraftValue(""); setFilterDraftValue2("");
        setPage(1);
    };

    const removeFilterChip = (cid) => { setFilterChips(filterChips.filter((c) => c.id !== cid)); setPage(1); };

    const buildExportQS = () => {
        const params = new URLSearchParams();
        if (search) params.set("search", search);
        const activeChips = filterChips.filter((c) => c.op === "is_empty" || c.op === "is_not_empty" || (Array.isArray(c.value) ? c.value.some((v) => v !== "" && v != null) : c.value !== "" && c.value != null));
        if (activeChips.length) params.set("filters", JSON.stringify(activeChips.map(({ field, op, value }) => ({ field, op, value }))));
        if (visibleColumns.length) params.set("columns", visibleColumns.map((c) => c.field_name).join(","));
        if (sortBy) { params.set("sort_by", sortBy); params.set("sort_dir", String(sortDir)); }
        const qs = params.toString();
        return qs ? `?${qs}` : "";
    };

    const toggleSort = (fieldName) => {
        if (sortBy === fieldName) {
            setSortDir(sortDir === 1 ? -1 : 1);
        } else {
            setSortBy(fieldName); setSortDir(1);
        }
        setPage(1);
    };

    const numericFields = useMemo(
        () => fields.filter((f) => ["number", "decimal"].includes(f.field_type)),
        [fields],
    );

    const isNumericField = (fname) => numericFields.some((f) => f.field_name === fname);

    const persistFormulas = (next) => {
        setFormulas(next);
        try { localStorage.setItem(FORMULA_KEY, JSON.stringify(next)); }
        catch (e) { console.warn("[records] failed to persist formulas to localStorage:", e); }
    };

    const addFormula = () => {
        if (!draftField) { toast.error("Pick a field"); return; }
        // Guard: numeric ops only on numeric fields.
        const op = (draftOp === "count") ? "count" : (isNumericField(draftField) ? draftOp : "count");
        const exists = formulas.some((f) => f.op === op && f.field === draftField);
        if (exists) { toast.message("That formula is already added"); return; }
        persistFormulas([...formulas, { op, field: draftField }]);
        setDraftField("");
    };

    const removeFormula = (idx) => {
        persistFormulas(formulas.filter((_, i) => i !== idx));
    };

    const loadAggregates = async () => {
        if (formulas.length === 0) { setAggResults([]); setAggTotal(0); return; }
        try {
            const spec = formulas.map((f) => `${f.op}:${f.field}`).join(",");
            const { data } = await api.get(`/forms/${id}/records/aggregates`, { params: { fields: spec } });
            setAggResults(data.results || []);
            setAggTotal(data.total_records || 0);
        } catch (err) {
            if (err?.response?.status && err.response.status !== 403) {
                console.warn("[records] aggregates fetch failed:", err);
            }
            setAggResults([]);
        }
    };

    useEffect(() => {
        if (formulasOpen) loadAggregates();
        // eslint-disable-next-line
    }, [formulasOpen, id, search, page, sortBy, sortDir, formulas]);

    const fmtAgg = (v) => {
        if (v == null) return "—";
        if (typeof v !== "number") return String(v);
        return Number.isInteger(v) ? v.toString() : v.toFixed(2);
    };

    const opGlyph = { sum: "Σ", avg: "x̄", min: "min", max: "max", count: "#" };
    const opAvailable = (fname) => {
        if (!fname) return ["count"];
        return isNumericField(fname) ? ["sum", "avg", "min", "max", "count"] : ["count"];
    };

    const fieldLabelByName = useMemo(() => {
        const m = {};
        fields.forEach((f) => { m[f.field_name] = f.field_label; });
        return m;
    }, [fields]);

    const lockedFields = useMemo(() => {
        const set = new Set();
        fields.forEach((f) => {
            if (f.default_group_value_enabled && groupDefaults[f.field_name] !== undefined) {
                set.add(f.field_name);
            }
        });
        return set;
    }, [fields, groupDefaults]);

    const wfStepControls = useMemo(() => {
        if (!editing || !form?.workflow_enabled) return { field: null, section: null };
        if (editing.workflow_status !== "pending") return { field: null, section: null };
        const steps = form.workflow_steps || [];
        const idx = editing.workflow_current_step ?? 0;
        const step = steps[idx];
        return {
            field: step?.field_controls && Object.keys(step.field_controls).length ? step.field_controls : null,
            section: step?.section_controls && Object.keys(step.section_controls).length ? step.section_controls : null,
        };
    }, [editing, form]);

    const useEditorPage = useMemo(() => {
        const lt = form?.layout_type || form?.layout_mode;
        return lt === "tabs" || lt === "stepper";
    }, [form]);

    const openNew = async () => {
        if (useEditorPage) {
            navigate(`/forms/${id}/records/new`);
            return;
        }
        // Re-fetch group defaults at click time so admin changes are picked up live
        const fresh = await loadGroupDefaults();
        setEditing(null);
        const seed = {};
        fields.forEach((f) => {
            if (f.default_group_value_enabled && fresh[f.field_name] !== undefined) {
                seed[f.field_name] = fresh[f.field_name];
            } else if (f.default_value != null) {
                seed[f.field_name] = f.default_value;
            }
        });
        setRecData(seed); setOpen(true);
    };
    const openEdit = (r) => {
        if (useEditorPage) {
            navigate(`/forms/${id}/records/${r.id}/edit`);
            return;
        }
        setEditing(r); setRecData(r.data || {}); setOpen(true);
    };

    const save = async () => {
        try {
            // visibility="everyone" → backend honors group default_record_visibility if set
            if (editing) await api.patch(`/forms/${id}/records/${editing.id}`, { data: recData });
            else await api.post(`/forms/${id}/records`, { data: recData, visibility: "everyone" });
            toast.success("Saved");
            setOpen(false); loadRecords();
        } catch (e) { toast.error(extractError(e)); }
    };

    const del = async (rid) => {
        if (!confirm("Delete record?")) return;
        try { await api.delete(`/forms/${id}/records/${rid}`); toast.success("Deleted"); loadRecords(); }
        catch (e) { toast.error(extractError(e)); }
    };

    if (!form) return <PageContainer><div className="text-sm text-muted-foreground">Loading…</div></PageContainer>;

    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    return (
        <PageContainer>
            <Link to={isAdmin ? "/forms" : "/portal"} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4" data-testid="records-back-link">
                <ArrowLeft size={12} /> {isAdmin ? "All forms" : "My apps"}
            </Link>
            <PageHeader
                overline={`Form · ${form.form_code}`}
                title={form.form_name}
                description={form.description || ""}
                actions={
                    <div className="flex gap-2 flex-wrap">
                        {isAdmin && <Link to={`/forms/${id}/builder`}><Button variant="outline" className="rounded-sm" data-testid="builder-link-btn">Builder</Button></Link>}
                        {isAdmin && (
                            <>
                                <Button
                                    variant="outline" className="rounded-sm"
                                    onClick={() => downloadFile(`/api/forms/${id}/records/csv-template`, `${form.form_code}_template.csv`)}
                                    data-testid="csv-template-btn"
                                    title="Download a CSV template with the header row"
                                ><FileXls size={14} className="mr-1" /> CSV template</Button>
                                <label htmlFor="csv-import-file" className="cursor-pointer">
                                    <Button
                                        type="button" variant="outline" className="rounded-sm pointer-events-none"
                                        data-testid="csv-import-btn"
                                    ><Plus size={14} className="mr-1" /> CSV import</Button>
                                </label>
                                <input
                                    id="csv-import-file" type="file" accept=".csv,text/csv" className="hidden"
                                    onChange={async (e) => {
                                        const f = e.target.files?.[0]; e.target.value = "";
                                        if (!f) return;
                                        try {
                                            const fd = new FormData();
                                            fd.append("file", f);
                                            const { data } = await api.post(`/forms/${id}/records/csv-import`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                                            setCsvEditorInitial(data);
                                            setCsvEditorOpen(true);
                                            if (data.ok) {
                                                toast.success(`${data.row_count} row${data.row_count === 1 ? "" : "s"} parsed and ready to import.`);
                                            } else {
                                                toast.message(`${(data.errors || []).length} validation issue${(data.errors || []).length === 1 ? "" : "s"} — fix inline and re-validate.`);
                                            }
                                        } catch (err) { toast.error(extractError(err)); }
                                    }}
                                    data-testid="csv-import-file-input"
                                />
                            </>
                        )}
                        <Button
                            variant="outline" className="rounded-sm"
                            onClick={() => {
                                const qs = buildExportQS();
                                downloadFile(`/api/forms/${id}/export/xlsx${qs}`, "records.xlsx");
                            }}
                            data-testid="export-xlsx-btn"
                        ><FileXls size={14} className="mr-1" /> Excel</Button>
                        <Button
                            variant="outline" className="rounded-sm"
                            onClick={() => {
                                const qs = buildExportQS();
                                downloadFile(`/api/forms/${id}/export/pdf${qs}`, "records.pdf");
                            }}
                            data-testid="export-pdf-btn"
                        ><FilePdf size={14} className="mr-1" /> PDF</Button>
                        {perms.can_create && (
                            <Button onClick={openNew} disabled={!form} className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="new-record-btn"><Plus size={16} className="mr-2" /> New record</Button>
                        )}
                    </div>
                }
                testid="records-header"
            />

            <div className="flex flex-wrap gap-2 mb-6">
                <div className="relative flex-1 min-w-[240px] max-w-md">
                    <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input placeholder="Search records…" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (setPage(1), loadRecords())} className="rounded-sm pl-9" data-testid="records-search-input" />
                </div>
                <Select value={sortBy || "__none__"} onValueChange={(v) => { setSortBy(v === "__none__" ? "" : v); }}>
                    <SelectTrigger className="w-44 rounded-sm" data-testid="sort-select"><SelectValue placeholder="Sort by" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__none__">Default (created)</SelectItem>
                        {sortableFields.map((f) => <SelectItem key={f.id} value={f.field_name}>{f.field_label}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Button variant="outline" className="rounded-sm" onClick={() => setSortDir(sortDir === 1 ? -1 : 1)} data-testid="sort-dir-btn">{sortDir === 1 ? "Asc" : "Desc"}</Button>
                <Button variant="outline" className="rounded-sm" onClick={() => { setPage(1); loadRecords(); }} data-testid="apply-filters-btn">Apply</Button>
                {filterableFields.length > 0 && (
                    <Button
                        variant={filterChips.length ? "default" : "outline"}
                        className={`rounded-sm ${filterChips.length ? "bg-primary text-primary-foreground hover:bg-primary/90" : ""}`}
                        onClick={() => setAddingFilter((v) => !v)}
                        data-testid="toggle-filter-add-btn"
                        title="Advanced filters"
                    >
                        <Funnel size={14} className="mr-1" /> Filter{filterChips.length ? ` · ${filterChips.length}` : ""}
                    </Button>
                )}
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="outline" className="rounded-sm" data-testid="view-variant-menu-btn">
                            <Eye size={14} className="mr-1" /> {variants.find((v) => v.id === activeVariantId)?.name || "View"}
                            <CaretDown size={10} className="ml-1" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="rounded-sm w-72" align="end">
                        <DropdownMenuLabel className="text-[10px] font-mono uppercase">Saved variants</DropdownMenuLabel>
                        <DropdownMenuItem onClick={clearVariant} data-testid="variant-clear">
                            <span className={!activeVariantId ? "font-medium" : ""}>All records (no variant)</span>
                        </DropdownMenuItem>
                        {variants.length === 0 && <DropdownMenuItem disabled>No variants yet</DropdownMenuItem>}
                        {variants.map((v) => (
                            <DropdownMenuItem key={v.id} onClick={() => applyVariant(v)} data-testid={`variant-apply-${v.id}`}>
                                <div className="flex items-center justify-between w-full gap-2">
                                    <div className="flex flex-col">
                                        <span className={activeVariantId === v.id ? "font-medium" : ""}>{v.name}</span>
                                        <span className="text-[10px] text-muted-foreground font-mono">
                                            {v.is_public ? "public" : "private"}{v.is_default ? " · default" : ""} · by {v.created_by_name || "—"}
                                        </span>
                                    </div>
                                    {(isAdmin || v.created_by === currentUserId) && (
                                        <button type="button" className="text-destructive hover:underline text-[10px]" onClick={(e) => { e.stopPropagation(); deleteVariant(v.id); }} data-testid={`variant-del-${v.id}`}>delete</button>
                                    )}
                                </div>
                            </DropdownMenuItem>
                        ))}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => setSaveVariantOpen(true)} data-testid="variant-save-current"><FloppyDisk size={12} className="mr-2" /> Save current view as variant…</DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
                <Button variant="outline" className="rounded-sm" onClick={() => setVariantPickerOpen((v) => !v)} data-testid="column-picker-btn" title="Pick columns">
                    <ListChecks size={14} className="mr-1" /> Columns
                </Button>
                <Button
                    variant={formulasOpen ? "default" : "outline"}
                    className={`rounded-sm ${formulasOpen ? "bg-primary text-primary-foreground hover:bg-primary/90" : ""}`}
                    onClick={() => setFormulasOpen((v) => !v)}
                    data-testid="toggle-formulas-btn"
                    title="Formula bar"
                >
                    <Sigma size={14} className="mr-1" /> Formulas{formulas.length ? ` · ${formulas.length}` : ""}
                </Button>
            </div>

            {/* Active filter chips */}
            {filterChips.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="filter-chips">
                    <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Filters:</span>
                    {filterChips.map((c) => {
                        const f = fields.find((ff) => ff.field_name === c.field);
                        const opLabel = (filterOpsFor(f?.field_type).find((o) => o.v === c.op)?.label) || c.op;
                        const valLabel = filterValueless(c.op) ? "" : (Array.isArray(c.value) ? `${c.value[0] ?? "…"} – ${c.value[1] ?? "…"}` : String(c.value));
                        return (
                            <div key={c.id} className="inline-flex items-center gap-2 border border-primary/30 bg-primary/5 rounded-sm px-2 py-1 text-xs" data-testid={`filter-chip-${c.id}`}>
                                <span className="font-medium">{f?.field_label || c.field}</span>
                                <span className="text-muted-foreground">{opLabel}</span>
                                {valLabel && <span className="font-mono">{valLabel}</span>}
                                <button type="button" onClick={() => removeFilterChip(c.id)} className="text-muted-foreground hover:text-destructive" data-testid={`filter-chip-remove-${c.id}`}><X size={10} /></button>
                            </div>
                        );
                    })}
                    <Button variant="ghost" size="sm" className="rounded-sm h-7 text-xs text-destructive" onClick={() => { setFilterChips([]); setPage(1); }} data-testid="filter-clear-all">Clear all</Button>
                </div>
            )}

            {/* Add-filter inline panel */}
            {addingFilter && filterableFields.length > 0 && (
                <div className="brut-section p-4 mb-4 bg-secondary/30" data-testid="filter-add-panel">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <Funnel size={14} className="text-primary" />
                            <div className="label-overline">Add filter</div>
                        </div>
                        <button type="button" onClick={() => setAddingFilter(false)} className="text-[10px] text-muted-foreground hover:text-foreground" data-testid="filter-add-close"><X size={12} /></button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-[1fr_140px_1fr_auto] gap-2 items-end">
                        <div>
                            <div className="label-overline mb-1">Field</div>
                            <Select value={filterDraftField} onValueChange={(v) => {
                                setFilterDraftField(v);
                                const f = fields.find((ff) => ff.field_name === v);
                                const ops = filterOpsFor(f?.field_type);
                                setFilterDraftOp(ops[0]?.v || "contains");
                                setFilterDraftValue(""); setFilterDraftValue2("");
                            }}>
                                <SelectTrigger className="rounded-sm" data-testid="filter-field-select"><SelectValue placeholder="Pick a field" /></SelectTrigger>
                                <SelectContent>
                                    {filterableFields.map((f) => <SelectItem key={f.id} value={f.field_name}>{f.field_label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <div className="label-overline mb-1">Operator</div>
                            <Select value={filterDraftOp} onValueChange={setFilterDraftOp} disabled={!filterDraftField}>
                                <SelectTrigger className="rounded-sm" data-testid="filter-op-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {(() => {
                                        const f = fields.find((ff) => ff.field_name === filterDraftField);
                                        return filterOpsFor(f?.field_type).map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>);
                                    })()}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <div className="label-overline mb-1">Value</div>
                            {(() => {
                                const f = fields.find((ff) => ff.field_name === filterDraftField);
                                if (!filterDraftField || filterValueless(filterDraftOp)) {
                                    return <Input disabled placeholder={filterValueless(filterDraftOp) ? "(no value needed)" : "Pick a field first"} className="rounded-sm" />;
                                }
                                if (filterDraftOp === "between") {
                                    const type = (f?.field_type === "number" || f?.field_type === "decimal") ? "number" : (f?.field_type === "date" ? "date" : (f?.field_type === "datetime" ? "datetime-local" : "text"));
                                    return (
                                        <div className="grid grid-cols-2 gap-2">
                                            <Input type={type} value={filterDraftValue} onChange={(e) => setFilterDraftValue(e.target.value)} placeholder="from" className="rounded-sm" data-testid="filter-value-from" />
                                            <Input type={type} value={filterDraftValue2} onChange={(e) => setFilterDraftValue2(e.target.value)} placeholder="to" className="rounded-sm" data-testid="filter-value-to" />
                                        </div>
                                    );
                                }
                                if (f?.field_type === "dropdown" || f?.field_type === "radio") {
                                    return (
                                        <Select value={String(filterDraftValue)} onValueChange={setFilterDraftValue}>
                                            <SelectTrigger className="rounded-sm" data-testid="filter-value-select"><SelectValue placeholder="Pick a value" /></SelectTrigger>
                                            <SelectContent>
                                                {(f.options || []).map((o) => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    );
                                }
                                if (f?.field_type === "toggle" || f?.field_type === "checkbox") {
                                    return (
                                        <Select value={String(filterDraftValue)} onValueChange={(v) => setFilterDraftValue(v === "true")}>
                                            <SelectTrigger className="rounded-sm" data-testid="filter-value-bool"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="true">true</SelectItem>
                                                <SelectItem value="false">false</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    );
                                }
                                const type = (f?.field_type === "number" || f?.field_type === "decimal") ? "number" : (f?.field_type === "date" ? "date" : (f?.field_type === "datetime" ? "datetime-local" : "text"));
                                return <Input type={type} value={filterDraftValue} onChange={(e) => setFilterDraftValue(e.target.value)} placeholder="Value" className="rounded-sm" data-testid="filter-value-input" />;
                            })()}
                        </div>
                        <Button type="button" onClick={addFilterChip} disabled={!filterDraftField} className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="filter-add-confirm-btn"><Plus size={12} className="mr-1" /> Add filter</Button>
                    </div>
                </div>
            )}

            {/* Column picker panel */}
            {variantPickerOpen && (
                <div className="brut-section p-4 mb-4 bg-secondary/30" data-testid="column-picker-panel">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <ListChecks size={14} className="text-primary" />
                            <div className="label-overline">Visible columns</div>
                        </div>
                        <div className="flex gap-2">
                            <button type="button" onClick={() => setVariantColumns(null)} className="text-[10px] text-muted-foreground hover:text-foreground font-mono" data-testid="column-picker-reset">Reset to auto</button>
                            <button type="button" onClick={() => setVariantPickerOpen(false)} className="text-[10px] text-muted-foreground hover:text-foreground" data-testid="column-picker-close"><X size={12} /></button>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                        {fields.filter((f) => !["table", "notes", "file", "image"].includes(f.field_type)).map((f) => {
                            const active = (variantColumns || autoVisibleColumns.map((c) => c.field_name)).includes(f.field_name);
                            return (
                                <label key={f.id} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-secondary/50 rounded-sm px-2 py-1" data-testid={`column-toggle-${f.field_name}`}>
                                    <Checkbox checked={active} onCheckedChange={() => toggleColumn(f.field_name)} />
                                    <span className={active ? "font-medium" : "text-muted-foreground"}>{f.field_label}</span>
                                </label>
                            );
                        })}
                    </div>
                    <p className="text-[10px] text-muted-foreground font-mono mt-3">Table, notes, file and image fields are not selectable here — open a record to view them.</p>
                </div>
            )}

            {formulasOpen && (
                <div className="brut-section p-4 mb-6 bg-secondary/30" data-testid="formula-bar">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <Sigma size={14} className="text-primary" />
                            <div className="label-overline">Formula bar</div>
                            <span className="text-[11px] text-muted-foreground">across {aggTotal || 0} visible records</span>
                        </div>
                        <button type="button" onClick={() => setFormulasOpen(false)} className="text-[10px] text-muted-foreground hover:text-foreground" data-testid="close-formula-bar"><X size={12} /></button>
                    </div>

                    {/* Active formula pills */}
                    {formulas.length === 0 ? (
                        <div className="text-xs text-muted-foreground border border-dashed border-border rounded-sm p-3 mb-3" data-testid="formula-empty">
                            No formulas yet. Pick a field + operation below.
                        </div>
                    ) : (
                        <div className="flex flex-wrap gap-2 mb-3" data-testid="formula-pills">
                            {formulas.map((f, idx) => {
                                const res = aggResults.find((r) => r.op === f.op && r.field === f.field);
                                return (
                                    <div key={`${f.op}:${f.field}`} className="inline-flex items-center gap-2 border border-border bg-card rounded-sm px-3 py-1.5 text-sm" data-testid={`formula-pill-${f.op}-${f.field}`}>
                                        <span className="font-mono text-[10px] text-primary uppercase">{opGlyph[f.op] || f.op}</span>
                                        <span className="text-xs">{fieldLabelByName[f.field] || f.field}</span>
                                        <span className="text-[10px] text-muted-foreground">=</span>
                                        <span className="font-mono text-sm font-semibold tabular-nums" data-testid={`formula-val-${f.op}-${f.field}`}>{fmtAgg(res?.value)}</span>
                                        <button type="button" onClick={() => removeFormula(idx)} className="text-muted-foreground hover:text-destructive" data-testid={`remove-formula-${idx}`}><X size={11} /></button>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Add formula */}
                    <div className="flex flex-wrap items-end gap-2 pt-3 border-t border-border" data-testid="formula-add-row">
                        <div className="min-w-[220px] flex-1">
                            <div className="label-overline mb-1.5">Field</div>
                            <Select value={draftField} onValueChange={(v) => {
                                setDraftField(v);
                                if (!isNumericField(v) && draftOp !== "count") setDraftOp("count");
                            }}>
                                <SelectTrigger className="rounded-sm" data-testid="formula-field-select"><SelectValue placeholder="Pick a field" /></SelectTrigger>
                                <SelectContent>
                                    {fields.length === 0 && <SelectItem value="__none__" disabled>No fields</SelectItem>}
                                    {fields.map((f) => (
                                        <SelectItem key={f.id} value={f.field_name}>
                                            {f.field_label} <span className="text-[10px] font-mono opacity-60">· {f.field_type}</span>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="min-w-[140px]">
                            <div className="label-overline mb-1.5">Operation</div>
                            <Select value={draftOp} onValueChange={setDraftOp}>
                                <SelectTrigger className="rounded-sm" data-testid="formula-op-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {opAvailable(draftField).map((op) => (
                                        <SelectItem key={op} value={op}>
                                            <span className="font-mono text-[10px] text-primary mr-2">{opGlyph[op]}</span>{op.toUpperCase()}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <Button type="button" onClick={addFormula} disabled={!draftField} className="rounded-sm h-10 bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="formula-add-btn">
                            <Plus size={12} className="mr-1" /> Add formula
                        </Button>
                        {formulas.length > 0 && (
                            <Button type="button" variant="ghost" size="sm" onClick={() => persistFormulas([])} className="rounded-sm h-10 text-destructive hover:text-destructive" data-testid="formula-clear-btn">
                                Clear all
                            </Button>
                        )}
                    </div>
                    <p className="text-[10px] font-mono text-muted-foreground mt-3">
                        sum / avg / min / max apply to number &amp; decimal fields. count works on any field.
                    </p>
                </div>
            )}

            {records.length === 0 ? (
                <EmptyState icon={Database} title="No records yet" description="Add the first one with the New record button." testid="records-empty" />
            ) : (
                <>
                    <div className="brut-section overflow-x-auto">
                        <table className="border-collapse" style={{ minWidth: "max-content" }} data-testid="records-table">
                            <thead><tr>
                                {visibleColumns.map((c) => {
                                    const isSorted = sortBy === c.field_name;
                                    const canSort = c.sortable;
                                    const w = colWidths[c.field_name] || 160;
                                    return (
                                        <th
                                            key={c.id}
                                            className={`data-header text-left relative ${canSort ? "cursor-pointer select-none hover:bg-secondary/50" : ""}`}
                                            style={{ width: w, minWidth: w, maxWidth: w }}
                                            onClick={canSort ? () => toggleSort(c.field_name) : undefined}
                                            data-testid={`th-${c.field_name}${canSort ? "-sortable" : ""}`}
                                            title={canSort ? "Click to sort · drag right edge to resize" : "Drag right edge to resize"}
                                        >
                                            <span className="inline-flex items-center gap-1 truncate">
                                                {c.field_label}
                                                {canSort && (
                                                    <span className="opacity-50">
                                                        {isSorted ? (sortDir === 1 ? <CaretUp size={10} /> : <CaretDown size={10} />) : <CaretUp size={9} className="opacity-30" />}
                                                    </span>
                                                )}
                                            </span>
                                            <ColumnResizer fieldName={c.field_name} width={w} setWidth={setColWidth} />
                                        </th>
                                    );
                                })}
                                <th className="data-header text-left" style={{ width: 120, minWidth: 120 }}>Visibility</th>
                                <th className="data-header text-left" style={{ width: 110, minWidth: 110 }}>Created</th>
                                <th className="data-header" style={{ width: 88, minWidth: 88 }}></th>
                            </tr>
                            </thead>
                            <tbody>
                                {records.map((r) => (
                                    <tr key={r.id} className={`hover:bg-secondary/50 ${perms.can_update ? "cursor-pointer" : ""}`} onClick={() => perms.can_update && openEdit(r)} data-testid={`record-row-${r.id}`}>
                                        {visibleColumns.map((c) => {
                                            const w = colWidths[c.field_name] || 160;
                                            let v = r.data?.[c.field_name];
                                            if (c.field_type === "table") {
                                                const rowsCount = Array.isArray(v) ? v.length : 0;
                                                return <td key={c.id} style={{ width: w, minWidth: w, maxWidth: w }} className="data-cell text-sm"><Badge variant="outline" className="rounded-sm font-mono text-[10px]">{rowsCount} row{rowsCount === 1 ? "" : "s"}</Badge></td>;
                                            }
                                            if ((c.field_type === "file" || c.field_type === "image") && typeof v === "string" && v.startsWith("file:")) {
                                                const fid = v.slice(5);
                                                return <td key={c.id} style={{ width: w, minWidth: w, maxWidth: w }} className="data-cell text-sm" onClick={(e) => e.stopPropagation()}>
                                                    <a href={`${BASE}/api/files/${fid}`} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-1 font-mono text-xs">
                                                        <FilePdf size={11} /> file
                                                    </a>
                                                </td>;
                                            }
                                            if (Array.isArray(v)) v = v.join(", ");
                                            if (typeof v === "boolean") v = v ? "Yes" : "No";
                                            return <td key={c.id} style={{ width: w, minWidth: w, maxWidth: w }} className="data-cell text-sm truncate" title={v != null ? String(v) : ""}>{v ?? "—"}</td>;
                                        })}
                                        <td className="data-cell" style={{ width: 120, minWidth: 120 }}><Badge variant="outline" className="rounded-sm font-mono text-[10px]">{r.visibility}</Badge></td>
                                        <td className="data-cell font-mono text-xs text-muted-foreground" style={{ width: 110, minWidth: 110 }}>{r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</td>
                                        <td className="data-cell text-right whitespace-nowrap" style={{ width: 88, minWidth: 88 }}>
                                            <Button
                                                variant="ghost" size="icon" className="rounded-sm"
                                                onClick={(e) => { e.stopPropagation(); downloadFile(`/api/forms/${id}/records/${r.id}/export/pdf`, `record-${r.id.slice(0, 8)}.pdf`); }}
                                                data-testid={`pdf-record-${r.id}`}
                                                title="Download record PDF"
                                            ><FilePdf size={14} /></Button>
                                            {perms.can_delete && (
                                                <Button variant="ghost" size="icon" className="rounded-sm" onClick={(e) => { e.stopPropagation(); del(r.id); }} data-testid={`del-record-${r.id}`}><Trash size={14} /></Button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="flex items-center justify-between mt-4 text-sm">
                        <div className="text-muted-foreground">Total: {total} · Page {page}/{totalPages}</div>
                        <div className="flex gap-2">
                            <Button variant="outline" size="sm" className="rounded-sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="records-prev">Previous</Button>
                            <Button variant="outline" size="sm" className="rounded-sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="records-next">Next</Button>
                        </div>
                    </div>
                </>
            )}

            <CsvImportEditor
                open={csvEditorOpen}
                onOpenChange={setCsvEditorOpen}
                formId={id}
                initial={csvEditorInitial}
                onCommitted={() => { setPage(1); loadRecords(); }}
            />

            <Dialog open={saveVariantOpen} onOpenChange={setSaveVariantOpen}>
                <DialogContent className="rounded-sm max-w-md" data-testid="save-variant-dialog">
                    <DialogHeader><DialogTitle>Save view as variant</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <div className="label-overline mb-1.5">Name</div>
                            <Input
                                value={saveDraft.name}
                                onChange={(e) => setSaveDraft({ ...saveDraft, name: e.target.value })}
                                placeholder="e.g. Open tickets — Asia"
                                className="rounded-sm"
                                data-testid="save-variant-name-input"
                                autoFocus
                                maxLength={80}
                            />
                        </div>
                        {isAdmin && (
                            <div className="space-y-2 text-sm">
                                <label className="flex items-center gap-2 cursor-pointer" data-testid="save-variant-public-row">
                                    <Checkbox
                                        checked={saveDraft.is_public}
                                        onCheckedChange={(v) => setSaveDraft({ ...saveDraft, is_public: !!v })}
                                        data-testid="save-variant-public-cb"
                                    />
                                    <span>Make public (visible to everyone with access to this form)</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer" data-testid="save-variant-default-row">
                                    <Checkbox
                                        checked={saveDraft.is_default}
                                        onCheckedChange={(v) => setSaveDraft({ ...saveDraft, is_default: !!v })}
                                        data-testid="save-variant-default-cb"
                                    />
                                    <span>Set as default (auto-applied on first load)</span>
                                </label>
                            </div>
                        )}
                        <div className="text-[10px] text-muted-foreground font-mono">
                            Captures: {visibleColumns.length} column{visibleColumns.length === 1 ? "" : "s"} · {filterChips.length} filter{filterChips.length === 1 ? "" : "s"}{sortBy ? ` · sort: ${sortBy} ${sortDir === 1 ? "asc" : "desc"}` : ""}{search ? ` · search: "${search}"` : ""}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" className="rounded-sm" onClick={() => setSaveVariantOpen(false)} data-testid="save-variant-cancel-btn">Cancel</Button>
                        <Button className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground" onClick={saveVariant} disabled={!saveDraft.name.trim()} data-testid="save-variant-save-btn">Save variant</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent
                    className={`rounded-sm max-h-[92vh] overflow-y-auto ${
                        ["tabs", "stepper"].includes(form?.layout_type) ? "max-w-6xl w-[95vw]" : "max-w-3xl"
                    }`}
                    data-testid="record-dialog"
                >
                    <DialogHeader><DialogTitle>{editing ? "Edit record" : "New record"}</DialogTitle></DialogHeader>
                    <div className="space-y-5">
                        <DynamicFormRenderer
                            sections={sections} fields={fields} data={recData}
                            onChange={setRecData} lockedFields={lockedFields}
                            layoutMode={form?.layout_type || form?.layout_mode || "single"}
                            fieldControls={wfStepControls.field}
                            sectionControls={wfStepControls.section}
                            formId={id}
                            recordId={editing?.id || null}
                        />
                        {editing && (
                            <RecordCommentsThread formId={id} recordId={editing.id} />
                        )}
                        <DialogFooter><Button onClick={save} className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="save-record-btn">Save record</Button></DialogFooter>
                    </div>
                </DialogContent>
            </Dialog>
        </PageContainer>
    );
}
